import {
  classColoursToLinearGradient,
  getClassColours,
  getClassTeachers,
  getNumberOfStudents,
} from "@/lib/class-utilities";
import { useRouter } from "@/lib/next-nprogress";
import useLocalStorageState from "@/lib/useLocalStorageState";
import {
  classNames,
  isAdmin,
  isAssistant,
  isStudent,
  isTeacher,
  sortAlphabetically,
  sortAlphabeticallyReversed,
  sortBy,
  sortByNewest,
  sortByOldest,
} from "@/lib/util";
import { useUpdateSchoolMutation } from "@/queries/autogenerate/graphql";
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AcademicCapIcon,
  ArchiveBoxIcon,
  BookOpenIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  PencilIcon,
  PlusCircleIcon,
  PlusIcon,
  TrashIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import chroma from "chroma-js";
import mixpanel from "mixpanel-browser";
import { nanoid } from "nanoid";
import Link from "next/link";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import toast from "react-hot-toast";
import Select from "react-select";
import styled from "styled-components";
import { InformationCircleComponent } from "./Commons";
import DialogComponent from "./Dialog";
import DragNDrop, { SortableColumn, SortableItem } from "./DragNDrop";
import ErrorMessage from "./ErrorMessage";
import { CurrentUser } from "./Page";
import { TitleBar } from "./styles/Layout";
import Tooltip from "./styles/Tooltip";
import AllergiesIcon from "./svg/AllergiesIcon";
import DragIndicatorIcon from "./svg/DragIndicatorIcon";
import YoutubeSearchedForIcon from "./svg/YoutubeSearchedForIcon";
import TextToggle from "./TextToggle";

const Grid = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: stretch;
  flex-wrap: wrap;

  & > * {
    margin: 10px;
  }

  & > div > a,
  & > div > button {
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    padding: 10px;
    width: 300px;
    min-height: 125px;
    height: 100%;

    h3,
    small.name {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;
      overflow: hidden;
    }

    h3 {
      -webkit-line-clamp: 2;
    }

    small.students {
      width: 80px;
    }
  }
  & > div > a,
  & > div > button,
  & a[data-add] {
    transition: all 0.1s ease;
  }

  & > div > a > div,
  & > div > button > div {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
  }

  & a[data-add] {
    width: 275px;
    min-height: 125px;
  }

  & svg {
    transition: all 0.1s ease;
  }

  & a[data-add]:hover svg {
    transform: scale(1.4);
  }
`;

const getClasses = (
  classSections: (NonNullable<
    CurrentUser["school"]
  >["classSections"][number] & {
    classes: NonNullable<CurrentUser["school"]>["classes"];
  })[]
): NonNullable<CurrentUser["school"]>["classes"] =>
  classSections.reduce(
    (acc, section) => [
      ...acc,
      ...section.classes.map((c) => ({
        ...c,
        classSection:
          section.id == "sectionLess"
            ? undefined
            : { ...section, classes: undefined },
      })),
    ],
    []
  );

interface Props {
  user: CurrentUser;
}

const Classes: React.FC<Props> = ({ user }) => {
  const [orderBy, setOrderBy] = useLocalStorageState("classesOrderBy", {
    label: "Alphabetical",
    value: "name_ASC",
  });

  return (
    <ClassesInner
      key={orderBy.value}
      user={user}
      orderBy={orderBy}
      setOrderBy={setOrderBy}
    />
  );
};

const UUIDfy = (
  sections: (NonNullable<CurrentUser["school"]>["classSections"][number] & {
    classes: NonNullable<CurrentUser["school"]>["classes"];
  })[]
) =>
  sections.map((cs) => ({
    ...cs,
    uuid: cs.id,
    classes: cs.classes.map((c) => ({ ...c, uuid: c.id })),
  }));

interface ClassesInnerProps {
  user: CurrentUser;
  orderBy: {
    label: string;
    value: string;
  };
  setOrderBy: (newValue: { label: string; value: string }) => void;
}

const ClassesInner: React.FC<ClassesInnerProps> = ({
  user,
  orderBy,
  setOrderBy,
}) => {
  const [showAllClasses, setShowAllClasses] = useLocalStorageState(
    "showAllClasses",
    true
  );
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);

  const [reorderPulse, setReorderPulse] = useState(false);

  const [isAddingSection, setAddingSection] = useState(false);
  const [sectionName, setSectionName] = useState("");

  const [showDialog, setShowDialog] = useState(false);
  const open = () => setShowDialog(true);
  const close = () => setShowDialog(false);

  const [{ fetching, error }, updateSchool] = useUpdateSchoolMutation();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { classes: originalClasses, classSections: originalSections } =
    user.school as NonNullable<CurrentUser["school"]>;

  const unsortedClasses =
    (isAdmin(user) || isAssistant(user)) && showAllClasses
      ? originalClasses
      : originalClasses.filter((c) => c.users.some((u) => u.id === user.id));

  const sortFunctions = {
    name_ASC: sortAlphabetically,
    name_DESC: sortAlphabeticallyReversed,
    createdAt_ASC: sortByOldest,
    createdAt_DESC: sortByNewest,
    teacher_ASC: (
      a: NonNullable<CurrentUser["school"]>["classes"][number],
      b: NonNullable<CurrentUser["school"]>["classes"][number]
    ) =>
      sortAlphabetically(
        a.users.find((u) => (u.roles ? isTeacher(u) : false)),
        b.users.find((u) => (u.roles ? isTeacher(u) : false))
      ),
  };
  const classes = useMemo(
    () =>
      orderBy.value == "custom"
        ? unsortedClasses
        : [...unsortedClasses].sort(sortFunctions[orderBy.value]),
    [orderBy, unsortedClasses]
  );

  const generateSections = (
    classes: NonNullable<CurrentUser["school"]>["classes"],
    sections: NonNullable<CurrentUser["school"]>["classSections"]
  ) =>
    [
      {
        id: "sectionLess",
        name: "",
        position: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        classes: classes.filter((c) => !c.classSection),
      },
      ...sections.map((cs) => ({
        ...cs,
        classes: sortBy(
          classes.filter((c) => c.classSection?.id == cs.id),
          "position"
        ),
      })),
    ]
      .sort((a, b) =>
        a.id == "sectionLess" || b.id == "sectionLess"
          ? (a.classes[0]?.position ?? 0) > (b.classes[0]?.position ?? 0)
            ? 1
            : (a.classes[0]?.position ?? 0) < (b.classes[0]?.position ?? 0)
            ? -1
            : 0
          : a.position > b.position
          ? 1
          : a.position < b.position
          ? -1
          : 0
      )
      .filter((cs) =>
        isStudent(user)
          ? cs.id === "sectionLess" ||
            cs.classes.some((c) => c.users.some((u) => u.id === user.id))
          : true
      );

  const [classSections, setClassSections] = useState(
    UUIDfy(generateSections(classes, originalSections))
  );

  const updateSectionName = async (sectionId: string, newName: string) => {
    setReorderPulse(true);

    setClassSections((prevSections) =>
      prevSections.map((section) =>
        section.id === sectionId ? { ...section, name: newName } : section
      )
    );

    await updateSchool({
      where: { id: user.school?.id },
      data: {
        classSections: {
          update: [
            {
              where: { id: sectionId },
              data: { name: { set: newName } },
            },
          ],
        },
      },
    });

    setReorderPulse(false);
  };

  const deleteSection = async (sectionId: string) => {
    setReorderPulse(true);

    setClassSections((prevSections) =>
      prevSections.filter((section) => section.id !== sectionId)
    );

    await updateSchool({
      where: { id: user.school?.id },
      data: {
        classSections: {
          delete: [{ id: sectionId }],
        },
      },
    });

    setReorderPulse(false);
  };

  const confirmUpdates = async (
    newClassSections: (NonNullable<
      CurrentUser["school"]
    >["classSections"][number] & {
      classes: NonNullable<CurrentUser["school"]>["classes"];
    })[]
  ) => {
    setReorderPulse(true);

    // We need to get the classes in the new sections
    // So we can accurately update their classSection and position
    const classes = getClasses(newClassSections);

    // We have to make sure we are getting the original details...
    // ...and not those of the updated class sections
    // const oldClasses = getClasses(generateSections(classes, originalSections));

    await updateSchool({
      where: { id: user.school?.id },
      data: {
        classSections: {
          update: newClassSections
            .filter((rs) => rs.id !== "sectionLess")
            .map((rs, index) => ({
              where: { id: rs.id },
              data: {
                position: { set: index + 1 },
              },
            })),
        },
        classes: {
          update: classes.map((c, index) => {
            // const oldClass = oldClasses.find((oc) => oc.id == c.id);

            return {
              where: { id: c.id },
              data: {
                position: { set: index },
                classSection: c.classSection?.id
                  ? {
                      connect: {
                        id: c.classSection.id,
                      },
                    }
                  : {
                      disconnect: {
                        id: { contains: "" }, // just get it to disconnect
                      },
                    },
                // : oldClass?.classSection?.id
                // ? {
                //     // TODO Confirm this works
                //     // disconnect: true,
                //     disconnect: {
                //       id: { equals: oldClass.classSection.id },
                //     },
                //   }
                // : undefined,
              },
            };
          }),
        },
      },
    });

    mixpanel.track("Reordered classes");

    setReorderPulse(false);
  };

  return (
    <div id="classes" className="print:hidden">
      <TitleBar>
        <div style={{ display: "flex", alignItems: "center" }}>
          <h1>
            Classes {classes.length ? <span>({classes.length})</span> : null}
          </h1>
          <Tooltip
            content={`You can access all your classes from this section. ${
              isTeacher(user)
                ? "You can also access the summary page from this section. The summary page allows you to see orders collated across all, or a specific subset, of the school's classes."
                : ""
            }`}
          >
            <span>
              <InformationCircleComponent />
            </span>
          </Tooltip>
        </div>
        <div className="flex items-center">
          {isTeacher(user) ? (
            <Link
              prefetch={false}
              href="/archived-classes"
              className="print:hidden"
            >
              <Tooltip content="Archived Classes">
                <button className="">
                  <ArchiveBoxIcon className="icon-md icon-gray" />
                </button>
              </Tooltip>
            </Link>
          ) : null}
          {isAdmin(user) || isAssistant(user) ? (
            <TextToggle
              checked={showAllClasses}
              checkedText="All Classes"
              uncheckedText="My Classes"
              onChange={() => setShowAllClasses(!showAllClasses)}
              inline={true}
              width="250px"
            />
          ) : null}
          <Select
            value={orderBy}
            options={[
              { label: "Alphabetical", value: "name_ASC" },
              { label: "Alphabetical (Descending)", value: "name_DESC" },
              { label: "Oldest", value: "createdAt_ASC" },
              { label: "Newest", value: "createdAt_DESC" },
              { label: "Teacher (Alphabetical)", value: "teacher_ASC" },
              { label: "Custom", value: "custom" },
            ]}
            noOptionsMessage={() => "No Options"}
            onChange={setOrderBy}
            isSearchable={false}
            isClearable={false}
            styles={{
              container: (provided) => ({
                ...provided,
                display: "inline-block",
                width: "250px",
                margin: "5px",
              }),
            }}
          />
        </div>
      </TitleBar>

      {/* Actions Bar, for selected */}
      <div
        className={classNames(
          "overflow-hidden transition-all",
          selectedClasses.length ? "h-12" : "h-0"
        )}
      >
        <div className="flex items-center justify-between px-1.5">
          <div className="flex items-center gap-1.5">
            <p className="font-normal text-gray-500">
              {selectedClasses.length} Classes Selected
            </p>
            <button
              className="btn btn-sm btn-subtle"
              onClick={() => setSelectedClasses([])}
              disabled={fetching}
            >
              Clear Selection
            </button>
            {selectedClasses.length < classes.length ? (
              <button
                className="btn btn-sm btn-subtle"
                onClick={() => {
                  setSelectedClasses(classes.map((c) => c.id));
                }}
                disabled={fetching}
              >
                Select All
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-1.5">
            {isTeacher(user) ? (
              <ArchiveClasses
                user={user}
                classes={selectedClasses}
                setSelectedClasses={setSelectedClasses}
              />
            ) : null}
            <LeaveClass
              user={user}
              classes={selectedClasses}
              setSelectedClasses={setSelectedClasses}
            />
          </div>
        </div>
      </div>
      {orderBy.value == "custom" ? (
        <div>
          {isTeacher(user) ? (
            <p className="my-1.5 flex w-max items-center gap-2 rounded bg-green-50 p-2 font-normal text-green-900">
              With custom sorting you can rearrange classes by dragging and
              dropping them.
            </p>
          ) : null}
          <DragNDrop
            collection={classSections}
            itemKey="classes"
            setCollection={(newClassSections) => {
              console.log("confirming updates");

              setClassSections(newClassSections);
              confirmUpdates(newClassSections);
            }}
          >
            {(classSections) => (
              <>
                {classSections.map((section, index) => (
                  <div
                    key={section.id}
                    className={classNames(reorderPulse ? "animate-pulse" : "")}
                  >
                    <ClassSectionComponent
                      key={section.id || "sectionLess"}
                      user={user}
                      index={index}
                      section={section}
                      fetching={fetching}
                      selectedClasses={selectedClasses}
                      setSelectedClasses={setSelectedClasses}
                      updateSectionName={updateSectionName}
                      deleteSection={deleteSection}
                    />
                  </div>
                ))}
                {isTeacher(user) ? (
                  <div className="group -ml-3 flex max-w-xl items-center gap-2.5 pb-8 pt-3">
                    {isAddingSection ? (
                      <div className="flex items-center">
                        <input
                          type="text"
                          autoFocus
                          value={sectionName}
                          onChange={(e) => setSectionName(e.target.value)}
                          className="rounded border border-solid border-gray-300 p-1.5"
                          disabled={fetching}
                        />
                        {sectionName ? (
                          <button
                            className="btn btn-primary"
                            onClick={async () => {
                              setReorderPulse(true);

                              const newSectionId = nanoid();

                              setClassSections((prevSections) => [
                                ...prevSections,
                                {
                                  id: newSectionId,
                                  uuid: newSectionId,
                                  name: sectionName,
                                  position:
                                    (user.school?.classSections?.length || 0) +
                                    1,
                                  createdAt: new Date(),
                                  updatedAt: new Date(),
                                  classes: [],
                                },
                              ]);

                              await updateSchool({
                                where: { id: user.school?.id },
                                data: {
                                  classSections: {
                                    create: [
                                      {
                                        id: newSectionId,
                                        name: sectionName,
                                        position:
                                          (user.school?.classSections?.length ||
                                            0) + 1,
                                      },
                                    ],
                                  },
                                },
                              });

                              mixpanel.track("Reordered class sections");

                              setReorderPulse(false);
                              setAddingSection(false);
                              setSectionName("");
                            }}
                            disabled={fetching}
                          >
                            <CheckCircleIcon className="w-5 text-green-700" />
                            Confirm
                          </button>
                        ) : null}
                        <button
                          className="btn"
                          onClick={() => {
                            setAddingSection(false);
                            setSectionName("");
                          }}
                          disabled={fetching}
                        >
                          <XCircleIcon className="w-5 text-gray-700" />
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        className="flex items-center"
                        onClick={() => setAddingSection(true)}
                      >
                        <PlusCircleIcon className="w-5 transform-gpu text-gray-700 transition group-hover:scale-125 group-hover:text-gray-900" />
                        <span className="font-semibold text-gray-700 transition group-hover:text-gray-800">
                          Add a Section
                        </span>
                      </button>
                    )}
                    <hr className="flex-1 border-gray-300" />
                  </div>
                ) : null}
              </>
            )}
          </DragNDrop>
        </div>
      ) : (
        <div>
          <ErrorMessage error={error} />
          <Grid>
            {classes.map((c) => (
              <div key={c.id} className="group relative">
                <ClassComponent
                  user={user}
                  c={c}
                  selectedClasses={selectedClasses}
                  setSelectedClasses={setSelectedClasses}
                />
              </div>
            ))}
            <Link
              prefetch={false}
              href={isTeacher(user) ? "/create-class" : "/join-class"}
              data-add
              className="flex transform cursor-pointer items-center justify-center rounded-lg bg-white shadow-md hover:scale-105"
            >
              <PlusIcon className="w-18 text-gray-400" />
            </Link>
          </Grid>
        </div>
      )}
    </div>
  );
};

interface AddClassProps {
  fauxId: string;
  user: CurrentUser;
  sectionId?: string;
}

const AddClass: React.FC<AddClassProps> = ({ fauxId, user, sectionId }) => {
  const router = useRouter();

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: fauxId, disabled: isStudent(user) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <a
      href="#"
      data-add
      className="flex transform cursor-pointer items-center justify-center rounded-lg bg-white shadow-md hover:scale-105"
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        e.preventDefault();
        router.push(
          isTeacher(user)
            ? `/create-class${
                sectionId ? "?sid=" + encodeURIComponent(sectionId) : ""
              }`
            : "/join-class"
        );
      }}
    >
      <PlusIcon className="w-18 text-gray-400" />
    </a>
  );
};

interface BaseClassContainerProps {
  user: CurrentUser;
  c: NonNullable<CurrentUser["school"]>["classes"][number];
  selectedClasses: string[];
  setSelectedClasses: Dispatch<SetStateAction<string[]>>;
}

interface DraggableClassContainerProps extends BaseClassContainerProps {
  index: number;
  sectionId: string;
}

const DraggableClassContainer: React.FC<DraggableClassContainerProps> = ({
  user,
  index,
  sectionId,
  c,
  selectedClasses,
  setSelectedClasses,
}) => {
  const isNew = true;

  return (
    <SortableItem id={c.id} index={index} column={sectionId}>
      {({ ref, handleRef, isDragging }) => (
        <div key={c.id} className="group relative" ref={ref}>
          <div
            className="absolute -top-5 left-1/2 z-10 flex w-min -translate-x-1/2 items-center justify-center rounded-full bg-white p-0.5 opacity-0 shadow-md transition delay-150 group-hover:opacity-100 print:hidden"
            title="Sort Class"
            ref={handleRef}
          >
            <Tooltip content="Drag To Sort" placement="top">
              <span>
                <DragIndicatorIcon className="icon-md" />
              </span>
            </Tooltip>
          </div>
          <ClassComponent
            user={user}
            c={c}
            selectedClasses={selectedClasses}
            setSelectedClasses={setSelectedClasses}
          />
        </div>
      )}
    </SortableItem>
  );
};

interface ClassComponentProps extends BaseClassContainerProps {}

const ClassComponent: React.FC<ClassComponentProps> = ({
  user,
  c,
  selectedClasses,
  setSelectedClasses,
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const open = () => setShowDialog(true);
  const close = () => setShowDialog(false);

  const [{ fetching, error }, updateSchool] = useUpdateSchoolMutation();

  const teachersList = getClassTeachers(c);
  const backgroundColours = getClassColours(c);

  const contrastWhite = chroma.contrast(backgroundColours[0], "white");
  const contrastBlack = chroma.contrast(backgroundColours[0], "black");

  return (
    <>
      <span>
        <Link
          prefetch={false}
          href={`/event-log?id=${c.id}`}
          className="absolute -left-5 -top-5 z-10 w-min rounded-full bg-white p-0.5 opacity-0 shadow-md transition delay-150 group-hover:opacity-100 print:hidden"
        >
          <Tooltip content="See Class Event Log" placement="left">
            <span>
              <YoutubeSearchedForIcon className="icon my-1.5" />
            </span>
          </Tooltip>
        </Link>
      </span>
      <Link
        prefetch={false}
        href="/schedule/[id]"
        as={`/schedule/${c.id}`}
        className={classNames(
          "transform rounded-lg bg-gray-300 text-black no-underline shadow-md group-hover:scale-105",
          selectedClasses.includes(c.id)
            ? "ring-2 ring-blue-400 ring-offset-2"
            : ""
        )}
        style={{
          background: classColoursToLinearGradient(backgroundColours, "45deg"),
          color: contrastWhite > contrastBlack ? "white" : "black",
        }}
      >
        <h3
          className={classNames(
            "line-clamp-3",
            contrastWhite > contrastBlack ? "text-white" : "text-black"
          )}
        >
          <BookOpenIcon className="w-6" /> {c.name}
        </h3>

        {c.metaRoom || c.metaSubject || c.metaAllergies || c.metaYearLevel ? (
          <small className="flex flex-wrap items-center gap-2.5">
            {c.metaRoom ? (
              <span className="flex items-center gap-0.5">
                <MapPinIcon className="w-4" /> {c.metaRoom}
              </span>
            ) : null}
            {c.metaSubject ? (
              <span className="flex items-center gap-0.5">
                <BookOpenIcon className="w-4" /> {c.metaSubject}
              </span>
            ) : null}
            {c.metaAllergies ? (
              <span className="flex items-center gap-0.5">
                <AllergiesIcon className="w-4" /> {c.metaAllergies}
              </span>
            ) : null}
            {c.metaYearLevel ? (
              <span className="flex items-center gap-0.5">
                <AcademicCapIcon className="w-4" /> {c.metaYearLevel}
              </span>
            ) : null}
          </small>
        ) : null}

        <div>
          <small className="name">
            {teachersList ? (
              <span>{teachersList}</span>
            ) : (
              <span>
                <ExclamationTriangleIcon className="w-4" /> No Teacher
              </span>
            )}
          </small>
          <small className="students">{getNumberOfStudents(c)} Students</small>
        </div>
      </Link>
      <span
        className="absolute right-0 top-0 -translate-x-0.5 translate-y-0.5 transform cursor-pointer bg-gray-300 py-0.5 pl-2 pr-0 opacity-0 transition-all duration-300 group-hover:opacity-100"
        style={{
          backgroundColor: backgroundColours[backgroundColours.length - 1],
          color: contrastWhite > contrastBlack ? "white" : "black",
        }}
      >
        <Link
          prefetch={false}
          href="/shopping-list/[id]"
          as={`/shopping-list/${c.id}`}
        >
          <Tooltip content="Shopping List">
            <span>
              <ClipboardDocumentListIcon
                className={`w-5 ${
                  contrastWhite > contrastBlack
                    ? "text-gray-100"
                    : "text-gray-800"
                }`}
              />
            </span>
          </Tooltip>
        </Link>
        {isTeacher(user) ? (
          <Link prefetch={false} href={`/schedule/${c.id}/settings`}>
            <Tooltip content="Settings">
              <span>
                <Cog6ToothIcon
                  className={`ml-1.5 w-5 ${
                    contrastWhite > contrastBlack
                      ? "text-gray-100"
                      : "text-gray-800"
                  }`}
                />
              </span>
            </Tooltip>
          </Link>
        ) : (
          <>
            <Tooltip content="Unenrol">
              <button
                className={`ml-1.5 w-5 ${
                  contrastWhite > contrastBlack
                    ? "text-gray-100"
                    : "text-gray-800"
                }`}
                onClick={() => open()}
              >
                <XMarkIcon className="w-5" />
              </button>
            </Tooltip>
            {showDialog ? (
              <DialogComponent onClose={close} aria-label="Confirm action">
                <p>Are you sure? Your orders in this class will be deleted.</p>
                <button className="btn btn-primary" onClick={close}>
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={async (e) => {
                    e.preventDefault();

                    await updateSchool({
                      where: { id: user.school?.id },
                      data: {
                        classes: {
                          update: [
                            {
                              where: { id: c.id },
                              data: {
                                users: {
                                  disconnect: [{ id: user.id }],
                                },
                                orders: {
                                  deleteMany: [
                                    {
                                      userId: {
                                        equals: user.id,
                                      },
                                    },
                                  ],
                                },
                              },
                            },
                          ],
                        },
                      },
                    });

                    mixpanel.track("Unenrolled from a class");
                  }}
                  disabled={fetching}
                >
                  {fetching ? <div className="spinner" /> : null}
                  Unenrol From This Class
                </button>
              </DialogComponent>
            ) : null}
          </>
        )}
        <input
          type="checkbox"
          name={`select-class-${c.id}`}
          id={`select-class-${c.id}`}
          checked={selectedClasses.includes(c.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedClasses([...selectedClasses, c.id]);
            } else {
              setSelectedClasses(selectedClasses.filter((id) => id !== c.id));
            }
          }}
          className="ml-1.5 align-middle"
        />
      </span>
    </>
  );
};

interface ClassSectionComponentProps {
  user: CurrentUser;
  index: number;
  section: NonNullable<CurrentUser["school"]>["classSections"][number] & {
    classes: NonNullable<CurrentUser["school"]>["classes"];
  };
  fetching: boolean;
  selectedClasses: string[];
  setSelectedClasses: Dispatch<SetStateAction<string[]>>;
  updateSectionName: (sectionId: string, newName: string) => Promise<void>;
  deleteSection: (sectionId: string) => Promise<void>;
}

const ClassSectionComponent: React.FC<ClassSectionComponentProps> = ({
  user,
  index,
  section,
  fetching,
  selectedClasses,
  setSelectedClasses,
  updateSectionName,
  deleteSection,
}) => {
  const [isEditingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState(section.name);

  const fauxId = `faux-${section.id}`;

  return (
    <SortableColumn key={section.id} id={section.id} index={index}>
      {({ ref, handleRef, isDropTarget }) => (
        <div ref={ref} className="mmmin-h-[200px]">
          {section.id != "sectionLess" ? (
            <div className="group flex max-w-xl items-center gap-2.5 py-1.5">
              {isEditingName ? (
                <form
                  className="flex items-center"
                  onSubmit={async (e) => {
                    e.preventDefault();

                    if (newName.trim().length > 0) {
                      if (newName != section.name) {
                        updateSectionName(section.id, newName);
                      }

                      setEditingName(false);
                    } else {
                      toast.error("Section name cannot be empty");
                    }
                  }}
                >
                  <input
                    type="text"
                    autoFocus
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="rounded border border-solid border-gray-300 p-1.5"
                    disabled={fetching}
                  />
                  {newName ? (
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={fetching}
                    >
                      <CheckCircleIcon className="w-5 text-green-700" />
                      Confirm
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      setEditingName(false);
                    }}
                    disabled={fetching}
                  >
                    <XCircleIcon className="w-5 text-gray-700" />
                    Cancel
                  </button>
                </form>
              ) : (
                <>
                  {isStudent(user) && !user.school?.permModifyRecipes ? null : (
                    <div
                      className="flex items-center justify-center"
                      title="Sort Sections"
                      ref={handleRef}
                    >
                      <DragIndicatorIcon className="w-3.5" />
                    </div>
                  )}
                  <h4 className="font-semibold text-gray-800">
                    {section.name || "Unnamed Section"}
                  </h4>
                  <Tooltip content="Edit Section Name">
                    <button onClick={() => setEditingName(true)} className="">
                      <PencilIcon className="w-0 text-gray-500 opacity-0 transition hover:text-gray-700 group-hover:w-5 group-hover:opacity-100" />
                    </button>
                  </Tooltip>
                </>
              )}
              <hr className="flex-1 border-gray-300" />
              {isEditingName ? null : (
                <Tooltip content="Remove Section">
                  <button
                    onClick={async () => {
                      await deleteSection(section.id);
                    }}
                    className=""
                    disabled={fetching}
                  >
                    <TrashIcon className="w-5 text-orange-500 opacity-0 transition hover:text-orange-700 group-hover:opacity-100" />
                  </button>
                </Tooltip>
              )}
            </div>
          ) : (
            <div className="group flex max-w-xl items-center gap-2.5 py-1.5">
              {isStudent(user) && !user.school?.permModifyRecipes ? null : (
                <div
                  className="flex items-center justify-center"
                  title="Sort Sections"
                  ref={handleRef}
                >
                  <DragIndicatorIcon className="w-3.5" />
                </div>
              )}
              <h4 className="font-semibold text-gray-800/30">
                Default Section
              </h4>
            </div>
          )}
          <Grid>
            {section.classes.length ? (
              section.classes.map((c, index) => (
                <DraggableClassContainer
                  key={c.id}
                  user={user}
                  index={index}
                  sectionId={section.id}
                  c={c}
                  selectedClasses={selectedClasses}
                  setSelectedClasses={setSelectedClasses}
                />
              ))
            ) : (
              <>
                <SortableItem id={section.id} index={0} column={section.id}>
                  {({ ref, handleRef, isDragging }) => (
                    <div
                      ref={ref}
                      className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gradient-to-tr from-cyan-50 to-gray-50 p-2"
                    >
                      <h3>Empty Section</h3>
                      <small>
                        Add classes to this section by dragging them.
                      </small>
                    </div>
                  )}
                </SortableItem>
              </>
            )}
            <AddClass
              fauxId={fauxId}
              user={user}
              sectionId={section.id == "sectionLess" ? undefined : section.id}
            />
          </Grid>
        </div>
      )}
    </SortableColumn>
  );
};

interface ArchiveClassesProps {
  classes: string[];
  user: CurrentUser;
  setSelectedClasses: Dispatch<SetStateAction<string[]>>;
}

const ArchiveClasses: React.FC<ArchiveClassesProps> = ({
  user,
  classes,
  setSelectedClasses,
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const open = () => setShowDialog(true);
  const close = () => setShowDialog(false);

  const [{ fetching, error }, updateSchool] = useUpdateSchoolMutation();

  if (error) return <ErrorMessage error={error} />;

  return (
    <>
      <button
        className="btn flex items-center gap-1.5 font-normal"
        onClick={open}
      >
        <ArchiveBoxIcon className="w-5" />
        Archive Selected Classes
      </button>
      <DialogComponent
        isOpen={showDialog}
        onClose={close}
        aria-label="Confirm action"
      >
        <p>Are you sure you want to archive the selected classes?</p>
        <button className="btn btn-primary" onClick={close}>
          Cancel
        </button>
        <button
          className="btn btn-warning"
          onClick={async () => {
            const { error } = await updateSchool({
              where: { id: user.school?.id },
              data: {
                classes: {
                  update: classes.map((id) => ({
                    where: { id },
                    data: {
                      archived: {
                        set: true,
                      },
                    },
                  })),
                },
              },
            });

            if (error) {
              console.error("There was an error: " + error.message);
            } else {
              close();
              setSelectedClasses([]);

              mixpanel.track("Archived many classes");
            }
          }}
          disabled={fetching}
        >
          {fetching ? <div className="spinner"></div> : null}
          Archive Classes
        </button>
      </DialogComponent>
    </>
  );
};

interface LeaveClassProps {
  classes: string[];
  user: CurrentUser;
  setSelectedClasses: Dispatch<SetStateAction<string[]>>;
}

const LeaveClass: React.FC<LeaveClassProps> = ({
  user,
  classes,
  setSelectedClasses,
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const open = () => setShowDialog(true);
  const close = () => setShowDialog(false);

  const [{ fetching, error }, updateSchool] = useUpdateSchoolMutation();

  if (error) return <ErrorMessage error={error} />;

  return (
    <>
      <button
        className={classNames(
          "btn flex items-center gap-1.5 font-normal",
          isTeacher(user) ? "btn-danger" : "btn-warning"
        )}
        onClick={open}
      >
        <TrashIcon className="w-5" />
        {isTeacher(user)
          ? "Delete Selected Classes"
          : "Unenroll From Selected Classes"}
      </button>
      <DialogComponent
        isOpen={showDialog}
        onClose={close}
        aria-label="Confirm action"
      >
        <p>
          Are you sure?
          {isTeacher(user) ? " This action cannot be undone." : null}
        </p>
        <button className="btn btn-primary" onClick={close}>
          Cancel
        </button>
        <button
          className={isTeacher(user) ? "btn btn-danger" : "btn btn-warning"}
          onClick={async () => {
            const { error } = await updateSchool({
              where: { id: user.school?.id },
              data: {
                classes: isTeacher(user)
                  ? {
                      delete: classes.map((id) => ({ id })),
                    }
                  : {
                      update: classes.map((id) => ({
                        where: { id },
                        data: {
                          users: {
                            disconnect: [{ id: user.id }],
                          },
                        },
                      })),
                    },
              },
            });

            if (error) {
              console.error("There was an error: " + error.message);
            } else {
              close();
              setSelectedClasses([]);

              mixpanel.track(
                isTeacher(user)
                  ? "Deleted many classes"
                  : "Unenrolled from many classes"
              );
            }
          }}
          disabled={fetching}
        >
          {fetching ? <div className="spinner"></div> : null}
          {isTeacher(user) ? "Delete Class" : "Unenroll From Class"}
        </button>
      </DialogComponent>
    </>
  );
};

export default Classes;
